/**
 * The catalog of the rights as the page shows them: sections, colours and drawings of the boards (Rights / Drawer / Main). The names and descriptions live in
 * `assets/i18n/access/{ru,en}.json` under `access.rights.<key>.*`; `cf_workflow_categories` is not grantable and is not shown.
 */
export type RightSection = 'cc' | 'stock' | 'catalog' | 'notif' | 'royalty' | 'sales';

export interface RightDef {
  key: string;
  section: RightSection;
  icon: string;
  /** The key is granted on one store at a time and arrives by the rule of the notifications (a grant on a workshop acts only on it). */
  storeScoped?: boolean;
  /** The grant carries resources: organizations (required, the auditor) or stores (optional). */
  resources?: 'organization' | 'store';
  /** Given to people only, never to «everybody of a group» (the notifications and the auditor): the same rule as `AccessCatalog.groupContainerAllowed` of the backend. */
  userOnly?: boolean;
}

export interface SectionDef { id: RightSection; tint: string; color: string }

export const SECTIONS: SectionDef[] = [
  { id: 'cc', tint: '#e6f4f2', color: '#0f766e' },
  { id: 'stock', tint: '#fdf0e8', color: '#c2410c' },
  { id: 'catalog', tint: '#fcebf3', color: '#be185d' },
  { id: 'notif', tint: '#eaf0f8', color: '#3b5b8c' },
  { id: 'royalty', tint: '#f1ebfd', color: '#6d28d9' },
  { id: 'sales', tint: '#e8f5ec', color: '#15803d' }
];

export const RIGHTS: RightDef[] = [
  { key: 'call_record_download', section: 'cc', icon: 'M4 15v-3a8 8 0 0 1 16 0v3M4 15h3v5H4zM17 15h3v5h-3z' },
  { key: 'callcenter_view_report', section: 'cc', icon: 'M4 20V10M10 20V4M16 20v-7M22 20H2' },
  { key: 'callcenter_manager', section: 'cc', icon: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21c1-4 4-6 7-6s6 2 7 6M17 3a4 4 0 0 1 0 8M22 21c-.5-2.5-2-4.5-4-5.5' },
  { key: 'feedback_support', section: 'cc', icon: 'M4 5h16v11H9l-5 4z' },
  { key: 'discard_product_input', section: 'stock', icon: 'M4 7h16M6 7l1 13h10l1-13M9 7V4h6v3' },
  { key: 'price_upsert_permission', section: 'stock', icon: 'M3 12l9-9h9v9l-9 9zM16 8h.01', resources: 'store' },
  { key: 'read_money_flow_report', section: 'stock', icon: 'M3 7h18v12H3zM3 11h18M7 15h3', resources: 'store' },
  { key: 'composite_product_edit', section: 'catalog', icon: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM15 19l4-4 2 2-4 4h-2z' },
  { key: 'create_order_notification', section: 'notif', icon: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4', storeScoped: true, userOnly: true },
  { key: 'decor_modified_notification', section: 'notif', icon: 'M4 20l4-1 11-11-3-3L5 16zM14 6l3 3', storeScoped: true, userOnly: true },
  { key: 'auditor', section: 'royalty', icon: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', resources: 'organization', userOnly: true },
  { key: 'cb_order_create_in_mn', section: 'sales', icon: 'M3 4h2l2.5 11h11L21 8H7M10 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM17 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z' }
];

export const HIDDEN_KEYS: readonly string[] = ['cf_workflow_categories'];
export const WIDE_NOTIFICATION_KEYS: readonly string[] = ['create_order_notification', 'decor_modified_notification'];

const BY_KEY = new Map(RIGHTS.map(r => [r.key, r] as const));
const SECTION_BY_ID = new Map(SECTIONS.map(s => [s.id, s] as const));
const UNKNOWN: RightDef = { key: '', section: 'cc', icon: 'M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18M12 8v5M12 16h.01' };

export function rightDef(key: string): RightDef {
  return BY_KEY.get(key) ?? { ...UNKNOWN, key };
}

/** May the right be given to «everybody of a group or a store»? The one place the dialogs ask; the backend answers `access.grant.containerNotAllowed` otherwise. */
export function groupAllowed(key: string | null | undefined): boolean {
  return !key || !rightDef(key).userOnly;
}

export function sectionOf(key: string): SectionDef {
  return SECTION_BY_ID.get(rightDef(key).section) ?? SECTIONS[0];
}

export function isVisibleKey(key: string): boolean {
  return !HIDDEN_KEYS.includes(key);
}

export function rightsOfSection(section: RightSection): RightDef[] {
  return RIGHTS.filter(r => r.section === section);
}

/** The apps of the page in the order the boards list them. */
export const APP_ORDER: readonly string[] = ['adm', 'manager', 'cc', 'cb', 'cf', 'kiosk', 'delivery', 'profile'];

export function sortApps(components: readonly string[]): string[] {
  return [...components].sort((a, b) => APP_ORDER.indexOf(a) - APP_ORDER.indexOf(b));
}

/** Where the right works, as the board Drawer tags it (`<b>КЦ</b> История звонков · Мониторинг`): the app and the screens, texts in `access.rights.<key>.where.*`. */
export const RIGHT_PLACES: Readonly<Record<string, readonly string[]>> = {
  call_record_download: ['cc', 'manager'],
  callcenter_view_report: ['cc'],
  callcenter_manager: ['cc'],
  feedback_support: ['manager'],
  discard_product_input: ['manager'],
  price_upsert_permission: ['manager'],
  read_money_flow_report: ['manager'],
  create_order_notification: [],
  decor_modified_notification: [],
  auditor: ['manager'],
  cb_order_create_in_mn: ['manager'],
  composite_product_edit: ['adm']
};
